import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Users,
  Euro,
  FileText,
  Target,
  Briefcase
} from 'lucide-react';

export default function PMESimplificationArticle() {
  return (
    <>
      <SEO 
        title="Simplification Marchés Publics pour les PME | Guide 2024"
        description="Les mesures de simplification des marchés publics pour faciliter l'accès des PME : allotissement, dématérialisation, paiement rapide."
        path="/blog/pme-simplification-marches-publics-2024"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-700">
                Stratégie
              </span>
              <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                NOUVEAU
              </span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              PME et Simplification 2026 : Nouvelles Opportunités des Marchés Publics
            </h1>
            <div className="flex items-center gap-6 text-slate-500">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                12 février 2026
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                11 min de lecture
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-12 mb-12 text-white text-center">
            <TrendingUp className="w-20 h-20 mx-auto mb-4 opacity-90" />
            <p className="text-xl font-medium">Réformes 2026 : Un tournant pour les PME</p>
            <p className="text-teal-100">Seuils relevés, CA plafonné, allotissement renforcé</p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              L'année 2026 marque un <strong>tournant historique</strong> pour l'accès des PME aux marchés publics. 
              Les décrets du 29 décembre 2025 et les évolutions réglementaires ouvrent de nouvelles opportunités : 
              seuils de dispense relevés, plafond de chiffre d'affaires réduit, allotissement encouragé. 
              Décryptage des mesures qui vont changer la donne.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <Users className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-teal-900 mb-2">Impact Très Fort - 9/10</h4>
                  <p className="text-teal-800">
                    Les réformes 2026 répondent directement aux demandes des fédérations de PME. 
                    L'objectif : <strong>30% des marchés publics attribués aux PME</strong>.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Target className="w-7 h-7 text-teal-600" />
              Les 5 mesures clés pour les PME en 2026
            </h2>

            <div className="space-y-6 my-8">
              <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Euro className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">1. Seuil de dispense à 60 000 € (fournitures/services)</h4>
                    <p className="text-slate-600">
                      Depuis le 1er avril 2026, les acheteurs peuvent passer des marchés jusqu'à 60 000 € sans publicité. 
                      <strong>Avantage PME</strong> : plus facile de se faire connaître en direct auprès des acheteurs locaux.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">2. Seuil travaux à 100 000 € pérennisé</h4>
                    <p className="text-slate-600">
                      Le seuil de 100 000 € pour les marchés de travaux, initialement temporaire, est désormais permanent. 
                      <strong>Avantage PME</strong> : accès simplifié aux petits chantiers publics.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">3. Plafond CA ramené à 1,5x le montant du marché</h4>
                    <p className="text-slate-600">
                      Les acheteurs ne peuvent plus exiger un CA supérieur à 1,5 fois le montant du marché (contre 2 fois avant). 
                      <strong>Avantage PME</strong> : accès à des marchés plus importants avec un CA plus modeste.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">4. Allotissement renforcé</h4>
                    <p className="text-slate-600">
                      La pression des fédérations PME pousse les acheteurs à découper les marchés en lots plus petits. 
                      <strong>Avantage PME</strong> : possibilité de candidater sur des lots à sa taille.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-2">5. Critère environnemental comme levier</h4>
                    <p className="text-slate-600">
                      L'obligation du critère vert (août 2026) avantage les PME engagées dans des démarches RSE. 
                      <strong>Avantage PME</strong> : différenciation sur la valeur plutôt que sur le prix seul.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Impact concret : exemples chiffrés
            </h2>

            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 my-8">
              <h4 className="font-bold text-teal-900 mb-4">Exemple 1 : Accès à un marché de services de 200 000 €</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4">
                  <h5 className="font-bold text-red-900 mb-2">Avant 2026</h5>
                  <p className="text-red-800 text-sm">CA minimum exigible : <strong>400 000 €</strong></p>
                  <p className="text-red-700 text-sm mt-2">Une PME avec 350 000 € de CA ne pouvait pas candidater.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h5 className="font-bold text-green-900 mb-2">Après 2026</h5>
                  <p className="text-green-800 text-sm">CA minimum exigible : <strong>300 000 €</strong></p>
                  <p className="text-green-700 text-sm mt-2">La même PME peut désormais candidater !</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 my-8">
              <h4 className="font-bold text-teal-900 mb-4">Exemple 2 : Marché de fournitures de 55 000 €</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4">
                  <h5 className="font-bold text-red-900 mb-2">Avant avril 2026</h5>
                  <p className="text-red-800 text-sm">Procédure : <strong>MAPA avec publicité</strong></p>
                  <p className="text-red-700 text-sm mt-2">Publication obligatoire, mise en concurrence formalisée.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h5 className="font-bold text-green-900 mb-2">Après avril 2026</h5>
                  <p className="text-green-800 text-sm">Procédure : <strong>Gré à gré possible</strong></p>
                  <p className="text-green-700 text-sm mt-2">L'acheteur peut consulter directement les entreprises locales.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Stratégies gagnantes pour les PME en 2026
            </h2>

            <div className="space-y-4 my-8">
              <div className="flex items-start gap-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h4 className="font-bold text-teal-900">Développez votre réseau local</h4>
                  <p className="text-teal-800">Avec les seuils relevés, les acheteurs consultent davantage en direct. Faites-vous connaître auprès des collectivités de votre territoire.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h4 className="font-bold text-teal-900">Ciblez les marchés allotis</h4>
                  <p className="text-teal-800">Surveillez les appels d'offres découpés en lots. Un marché de 500 000 € en 5 lots de 100 000 € est accessible à votre taille.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h4 className="font-bold text-teal-900">Valorisez vos engagements RSE</h4>
                  <p className="text-teal-800">Le critère environnemental obligatoire est une opportunité. Certifications ISO 14001, labels : capitalisez sur vos atouts.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h4 className="font-bold text-teal-900">Envisagez le groupement</h4>
                  <p className="text-teal-800">Pour les marchés plus importants, constituez un groupement avec d'autres PME complémentaires.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">5</div>
                <div>
                  <h4 className="font-bold text-teal-900">Automatisez votre veille</h4>
                  <p className="text-teal-800">Ne perdez plus de temps à chercher manuellement. Les outils de veille IA vous alertent sur les opportunités adaptées.</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Élections municipales mars 2026 : une fenêtre d'opportunité
            </h2>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 my-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-violet-900 mb-2">Contexte politique favorable</h4>
                  <p className="text-violet-800">
                    Les élections municipales de mars 2026 poussent les élus sortants à montrer leur soutien à l'économie locale. 
                    C'est le moment idéal pour se positionner auprès des collectivités qui renouvellent leurs politiques d'achat.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Tableau récapitulatif : PME et marchés publics 2026
            </h2>

            <div className="bg-slate-100 rounded-xl p-6 my-8 overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 text-slate-900">Mesure</th>
                    <th className="text-left py-3 text-slate-900">Avant</th>
                    <th className="text-left py-3 text-slate-900">Après 2026</th>
                    <th className="text-left py-3 text-slate-900">Bénéfice PME</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">Seuil fournitures/services</td>
                    <td className="py-3">40 000 €</td>
                    <td className="py-3 text-green-700 font-bold">60 000 €</td>
                    <td className="py-3">+50% de marchés en gré à gré</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">Seuil travaux</td>
                    <td className="py-3">100 000 € (temp.)</td>
                    <td className="py-3 text-green-700 font-bold">100 000 € (perm.)</td>
                    <td className="py-3">Sécurité juridique</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-3 font-medium">CA exigible</td>
                    <td className="py-3">2x montant</td>
                    <td className="py-3 text-green-700 font-bold">1,5x montant</td>
                    <td className="py-3">Accès à +25% de marchés</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">Critère prix seul</td>
                    <td className="py-3">Autorisé</td>
                    <td className="py-3 text-green-700 font-bold">Interdit (août 2026)</td>
                    <td className="py-3">Valorisation qualité/RSE</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
              Conclusion : 2026, l'année des PME
            </h2>

            <p>
              Les réformes 2026 constituent une <strong>opportunité historique</strong> pour les PME souhaitant 
              accéder à la commande publique. Les barrières à l'entrée s'abaissent, les critères de sélection 
              évoluent en faveur des entreprises engagées.
            </p>

            <p>
              Pour en profiter pleinement, les PME doivent dès maintenant :
            </p>

            <ul className="space-y-2 my-6">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-600" />
                <span>Se faire connaître des acheteurs locaux</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-600" />
                <span>Structurer leurs engagements RSE</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-600" />
                <span>Automatiser leur veille pour ne manquer aucune opportunité</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              PME : trouvez les marchés à votre taille
            </h3>
            <p className="text-teal-100 mb-6">
              Notre IA filtre 8+ sources et vous recommande les marchés adaptés à votre CA et votre secteur.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-slate-100">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
